/**
 * Customer-facing device names.
 *
 * The single source of truth is `device_models.display_name` in Supabase.
 * Those values come in a handful of shapes:
 *
 *   "iPhone 15 Pro"                               plain
 *   "iPad Pro 12.9-inch (4th generation) (2020)"  name + generation + year block
 *   "MacBook Air 13 inch M4 (2025)"               name + year block
 *   "iMac 24\" (M4, 2024)"                        name + (variant, year) block
 *   "Apple Watch Series 9 (Mainline, 2023)"       name + (segment, year) block
 *
 * The model key (the URL slug, e.g. "ipad-pro-129-inch-4th-generation") is an
 * identifier, not a name. Formatting it is only a last-resort fallback for
 * cart items that were stored before the display name travelled with them,
 * or for the split second before the model has been fetched.
 */

export interface DeviceNameParts {
  /** Everything before the trailing parenthesised block, e.g. "iPad Pro 12.9-inch (4th generation)". */
  base: string;
  /** Contents of the trailing block, e.g. "2020" or "M4, 2024". Empty when there is none. */
  meta: string;
}

const TRAILING_BLOCK = /\s*\(([^()]*)\)\s*$/;

/** Splits a display name into the main name and its trailing "(…)" block. */
export function splitDeviceDisplayName(name: string | null | undefined): DeviceNameParts {
  const raw = (name || '').trim();
  const match = raw.match(TRAILING_BLOCK);
  if (!match) return { base: raw, meta: '' };
  return {
    base: raw.slice(0, raw.length - match[0].length).trim(),
    meta: match[1].trim(),
  };
}

/**
 * Tidies a display name for running text, cart rows and e-mails.
 *
 *  - "(4th generation) (2020)" becomes "(4th generation, 2020)"
 *  - with `short`, the release year is dropped: "(M4, 2024)" → "(M4)",
 *    "(2020)" disappears, "(4th generation, 2020)" → "(4th generation)"
 */
export function formatDeviceDisplayName(
  name: string | null | undefined,
  options: { short?: boolean } = {}
): string {
  let result = (name || '').trim().replace(/\s+/g, ' ');
  if (!result) return '';

  // Merge two adjacent trailing blocks into one.
  result = result.replace(/\)\s*\(([^()]*)\)\s*$/, ', $1)');

  const { base, meta } = splitDeviceDisplayName(result);
  if (!meta) return base;

  let segments = splitMetaSegments(base, meta);
  if (options.short) segments = segments.filter((segment) => !isYear(segment));
  return segments.length ? `${base} (${segments.join(', ')})` : base;
}

/**
 * The line shown under a title: "M4 · 2024", "2020", "3e gen".
 * Segments that only repeat a word from the base name ("SE" under
 * "Apple Watch SE", "Ultra" under "Apple Watch Ultra 3") are dropped.
 */
export function formatDeviceNameMeta(base: string, meta: string): string {
  return splitMetaSegments(base, meta).join(' · ');
}

const isYear = (segment: string): boolean => /^(19|20)\d{2}$/.test(segment);

/** Splits "M4 Pro, 2024" into its segments, minus those already present in the base name. */
function splitMetaSegments(base: string, meta: string): string[] {
  const baseWords = new Set(
    base
      .toLowerCase()
      .split(/[^a-z0-9.]+/)
      .filter(Boolean)
  );
  return meta
    .split(',')
    .map((segment) => segment.trim())
    .filter((segment) => segment && !baseWords.has(segment.toLowerCase()));
}

/** True for slug-like identifiers such as "ipad-pro-129-inch-4th-generation". */
export function isModelKey(value: string | null | undefined): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test((value || '').trim());
}

const BRAND_TOKENS: Record<string, string> = {
  iphone: 'iPhone',
  ipad: 'iPad',
  imac: 'iMac',
  macbook: 'MacBook',
  airpods: 'AirPods',
  ipod: 'iPod',
  apple: 'Apple',
  watch: 'Watch',
  samsung: 'Samsung',
  galaxy: 'Galaxy',
  mac: 'Mac',
  intel: 'Intel',
};

const UPPER_TOKENS = new Set(['se', 'fe', 'gb', 'tb', '5g', '4g', 'lte', 'wifi', 'ssd']);

const INCH_SIZES: Record<string, string> = {
  '129': '12.9',
  '105': '10.5',
  '97': '9.7',
  '79': '7.9',
  '83': '8.3',
};

const ORDINAL = /^\d+(st|nd|rd|th)$/;

/**
 * Best-effort readable name from a model key. Only used when no display
 * name is available. Examples:
 *
 *   "ipad-pro-129-inch-4th-generation" → "iPad Pro 12.9-inch (4th generation)"
 *   "apple-watch-se-2nd-gen"           → "Apple Watch SE (2nd generation)"
 *   "macbook-pro-14-m4-max-2025"       → "MacBook Pro 14 M4 Max (2025)"
 *   "samsung-galaxy-s25-ultra"         → "Samsung Galaxy S25 Ultra"
 */
export function formatModelKeyFallback(key: string | null | undefined): string {
  const tokens = (key || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
    .split('-')
    .filter(Boolean);
  if (tokens.length === 0) return '';

  const words: string[] = [];
  let trailingYear: string | null = null;
  let generation: string | null = null;
  let sawAppleMobile = false;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const next = tokens[i + 1];
    const isLast = i === tokens.length - 1;

    if (token === 'iphone' || token === 'ipad') sawAppleMobile = true;

    // "129" + "inch" → "12.9-inch"
    if (next === 'inch' && (INCH_SIZES[token] || /^\d{1,2}$/.test(token))) {
      words.push(`${INCH_SIZES[token] || token}-inch`);
      i++;
      continue;
    }

    // "4th" + "generation"/"gen" → "(4th generation)"
    if (ORDINAL.test(token) && (next === 'generation' || next === 'gen')) {
      generation = `${token} generation`;
      i++;
      continue;
    }

    // Trailing release year → "(2025)"
    if (isLast && /^(19|20)\d{2}$/.test(token) && words.length > 0) {
      trailingYear = token;
      continue;
    }

    if (BRAND_TOKENS[token]) {
      words.push(BRAND_TOKENS[token]);
      continue;
    }
    if (UPPER_TOKENS.has(token)) {
      words.push(token.toUpperCase());
      continue;
    }
    // Chips and Samsung model codes: m4, a17, s25, z, flip7, fold6
    if (/^[msaz]\d*$/.test(token) || /^(flip|fold)\d+$/.test(token)) {
      words.push(token.charAt(0).toUpperCase() + token.slice(1));
      continue;
    }
    if (/^\d+$/.test(token)) {
      words.push(token);
      continue;
    }
    if (token === 'mini') {
      // Apple writes "iPad mini" and "iPhone 13 mini", but "Mac Mini".
      words.push(sawAppleMobile ? 'mini' : 'Mini');
      continue;
    }
    words.push(token.charAt(0).toUpperCase() + token.slice(1));
  }

  let result = words.join(' ');
  if (generation) result += ` (${generation})`;
  if (trailingYear) result += ` (${trailingYear})`;
  return result.trim();
}

/**
 * Picks the best available name: the database display name when present,
 * otherwise a readable version of the model key.
 */
export function resolveDeviceDisplayName(
  displayName: string | null | undefined,
  modelKey: string | null | undefined,
  options: { short?: boolean } = {}
): string {
  const trimmedDisplayName = (displayName || '').trim();
  if (trimmedDisplayName && !isModelKey(trimmedDisplayName)) {
    return formatDeviceDisplayName(trimmedDisplayName, options);
  }
  const raw = (modelKey || '').trim();
  if (!raw) return '';
  // A pre-formatted name may already have been stored in the modelName slot.
  if (!isModelKey(raw)) return formatDeviceDisplayName(raw, options);
  return formatDeviceDisplayName(formatModelKeyFallback(raw), options);
}
